import { devBus } from '../debug/devBus';
import type { TimeOfDayClock } from '../atmosphere/timeOfDayClock';

// Visitor-facing timeline (demo view only). Strips the dev NLE down to the two
// scene drivers a visitor can actually feel:
//   · the day cycle  — a playhead sweeping the full day length (clock.t), with
//                       the time-of-day chip on the right
//   · the era        — a PAST/PRESENT marker that warms→cools as the room morphs
// No clips, no beds, no scrubbing. Read-only.
//
// Hidden until the scene wakes (power:on — the generator pull-start). Before
// that the day clock is frozen and the room reads "past, dark", so a static bar
// would only clutter the opening; it fades in the instant the shop powers on.

const PAST = [232, 193, 112] as const;   // #e8c170 warm — matches the dev era ring
const PRESENT = [125, 184, 232] as const; // #7db8e8 cool

export interface DemoTimelineDeps {
  clock: TimeOfDayClock;
  /** Morph progress: 0 = past … 1 = present. */
  morph: () => number;
  /** Flip the era (past ⇄ present) — wired to the PAST/PRESENT marker's click.
   *  App-side this runs a normal timed setTarget, so the room morphs smoothly. */
  toggleEra: () => void;
  /** When false, the strip does NOT auto-reveal on the generator pull — the
   *  story-director reveals it as the reward chrome on RETURN (P0.5). Default
   *  true preserves the original D4 behavior (reveal on power) when there's no
   *  director. */
  revealOnPower?: boolean;
}

export interface DemoTimeline {
  /** Call once per frame (render loop) so the playhead tracks the running clock
   *  and the era marker tracks the morph. Write-on-change — a static frame
   *  touches no DOM. */
  update(): void;
  /** Fade the strip in + make the era toggle interactive (the reward reveal). */
  reveal(): void;
  /** Enable/disable dragging the day-cycle track to scrub the time of day
   *  (the reward turns this on; off during the guided loop). */
  setScrubbable(on: boolean): void;
  dispose(): void;
}

function mixRGB(t: number): [number, number, number] {
  const c = (i: number) => Math.round(PAST[i] + (PRESENT[i] - PAST[i]) * t);
  return [c(0), c(1), c(2)];
}

export function createDemoTimeline(deps: DemoTimelineDeps): DemoTimeline {
  const wrapper = document.createElement('div');
  wrapper.id = 'lec-demo-timeline';
  wrapper.style.cssText = [
    'position: fixed',
    'bottom: 22px',
    'left: 50%',
    'transform: translateX(-50%)',
    'display: flex',
    'align-items: center',
    'gap: 11px',
    'padding: 7px 13px',
    'background: rgba(20, 26, 38, 0.42)',
    'border: 1px solid rgba(150, 178, 228, 0.28)',
    'border-radius: 999px',
    'backdrop-filter: blur(7px)',
    '-webkit-backdrop-filter: blur(7px)',
    'font: 11px/1 system-ui, sans-serif',
    'color: rgba(234, 241, 255, 0.78)',
    'user-select: none',
    'pointer-events: none',
    'z-index: 1000',
    'opacity: 0',
    'transition: opacity 700ms ease',
  ].join('; ');

  // Era marker (left) — a toggle: click to flip past ⇄ present (app-side this
  // runs the timed morph). The wrapper is click-through (pointer-events: none)
  // so it never blocks the 3D scene; this button opts itself back in.
  const era = document.createElement('button');
  era.type = 'button';
  era.title = 'toggle era — past ⇄ present';
  era.style.cssText = [
    'min-width: 64px',
    'text-align: center',
    'text-transform: uppercase',
    'letter-spacing: 0.10em',
    'font-weight: 600',
    'font-size: 10px',
    'font-family: inherit',
    'line-height: 1',
    'padding: 3px 9px',
    'background: rgba(255, 255, 255, 0.05)',
    'border: 1px solid rgba(255, 255, 255, 0.14)',
    'border-radius: 999px',
    'cursor: pointer',
    'pointer-events: none', // off until reveal() — the era toggle is a reward (P0.5)
    'transition: background 140ms ease, border-color 140ms ease',
  ].join('; ');
  era.addEventListener('pointerenter', () => { era.style.background = 'rgba(255, 255, 255, 0.13)'; });
  era.addEventListener('pointerleave', () => { era.style.background = 'rgba(255, 255, 255, 0.05)'; });
  era.addEventListener('click', () => deps.toggleEra());

  // The day-cycle track (middle).
  const track = document.createElement('div');
  track.style.cssText = [
    'position: relative',
    'width: 220px',
    'height: 3px',
    'border-radius: 2px',
    'background: rgba(255, 255, 255, 0.16)',
  ].join('; ');
  const fill = document.createElement('div');
  fill.style.cssText = [
    'position: absolute',
    'left: 0',
    'top: 0',
    'height: 100%',
    'width: 0%',
    'border-radius: 2px',
    'opacity: 0.55',
  ].join('; ');
  const dot = document.createElement('div');
  dot.style.cssText = [
    'position: absolute',
    'top: 50%',
    'left: 0%',
    'width: 8px',
    'height: 8px',
    'border-radius: 50%',
    'transform: translate(-50%, -50%)',
  ].join('; ');
  track.appendChild(fill);
  track.appendChild(dot);

  // Time-of-day chip (right).
  const time = document.createElement('span');
  time.style.cssText = [
    'min-width: 38px',
    'font-variant-numeric: tabular-nums',
    'letter-spacing: 0.04em',
    'color: rgba(234, 241, 255, 0.62)',
  ].join('; ');

  wrapper.appendChild(era);
  wrapper.appendChild(track);
  wrapper.appendChild(time);
  document.body.appendChild(wrapper);

  // Reveal — fade in + make the era toggle interactive. The story-director calls
  // this as the reward chrome on RETURN (P0.5); without a director it falls back
  // to the original D4 behavior (reveal on the generator pull) below.
  const reveal = (): void => {
    wrapper.style.opacity = '1';
    era.style.pointerEvents = 'auto';
  };
  // Day-cycle scrub — off during the guided loop, turned on with the reward.
  let scrubbable = false;
  const setScrubbable = (on: boolean): void => {
    scrubbable = on;
    track.style.pointerEvents = on ? 'auto' : 'none';
    track.style.cursor = on ? 'pointer' : '';
  };
  let scrubbing = false;
  const scrubTo = (clientX: number): void => {
    const r = track.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / Math.max(1, r.width)));
    // W3 — the bar spans the daylight window: 0..1 across the track → [dayStart, dayEnd].
    deps.clock.t = deps.clock.dayStart + f * (deps.clock.dayEnd - deps.clock.dayStart);
  };
  track.addEventListener('pointerdown', (e) => {
    if (!scrubbable) return;
    scrubbing = true;
    try { track.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    scrubTo(e.clientX);
  });
  track.addEventListener('pointermove', (e) => { if (scrubbing) scrubTo(e.clientX); });
  const endScrub = (e: PointerEvent): void => {
    scrubbing = false;
    try { track.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  track.addEventListener('pointerup', endScrub);
  track.addEventListener('pointercancel', endScrub);

  // No director → reveal on the generator pull (original D4). With a director
  // (demo), revealOnPower is false and the director reveals it on RETURN.
  const unsubPower = deps.revealOnPower === false ? () => {} : devBus.on('power:on', reveal);

  // Write-on-change caches (PF6 discipline — a still scene writes nothing).
  let lastPct = -1;
  let lastTime = '';
  let lastTint = -1; // quantized morph (0..20) so mid-morph color writes are bounded

  const update = (): void => {
    const t = deps.clock.t;
    // W3 — the bar represents the daylight window: t ∈ [dayStart, dayEnd] → 0..100%.
    const span = Math.max(1e-6, deps.clock.dayEnd - deps.clock.dayStart);
    const pct = Math.round((Math.max(0, Math.min(1, (t - deps.clock.dayStart) / span))) * 1000) / 10;
    if (pct !== lastPct) {
      lastPct = pct;
      dot.style.left = `${pct}%`;
      fill.style.width = `${pct}%`;
    }
    const hm = deps.clock.formatHM();
    if (hm !== lastTime) { lastTime = hm; time.textContent = hm; }

    const m = deps.morph();
    const q = Math.round(m * 20);
    if (q !== lastTint) {
      lastTint = q;
      const [r, g, b] = mixRGB(m);
      const color = `rgb(${r}, ${g}, ${b})`;
      dot.style.background = color;
      dot.style.boxShadow = `0 0 7px ${color}`;
      fill.style.background = color;
      era.textContent = m >= 0.5 ? 'present' : 'past';
      era.style.color = color;
      era.style.borderColor = `rgba(${r}, ${g}, ${b}, 0.5)`; // faint era-tinted ring
    }
  };
  update(); // paint once so the first reveal isn't a frame behind

  return {
    update,
    reveal,
    setScrubbable,
    dispose() {
      unsubPower();
      wrapper.remove();
    },
  };
}
